
import { defineComponent } from 'vue';
import throttle from 'quasar/src/utils/throttle/throttle.js';;

interface GestureInfo {
	isFirst?: boolean;
	isFinal?: boolean;
	distance?: number;
	delta: {
		x: number;
		y: number;
	};
}

export default defineComponent({
	name: 'message-image',

	props: {
		src: String,

		isWidget: {
			type: Boolean,
			default: false
		}
	},

	emits: {
		loaded: null
	},

	data() {
		return {
			isFullscreen: false,
			zoomLevel: 1,
			panX: 0,
			panY: 0,
			isZooming: false,
			isDragging: false
		};
	},

	methods: {
		loadedImage() {
			this.$emit('loaded');
		},

		handleKeydown(event: KeyboardEvent) {
			if (event.key === 'Escape' && this.isFullscreen) this.closeFullscreen();
		},

		resetPanIfNeeded() {
			if (this.zoomLevel <= 1) {
				this.panX = 0;
				this.panY = 0;
			}
		},

		zoomIn() {
			this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
			this.resetPanIfNeeded();
		},

		zoomOut() {
			this.zoomLevel = Math.max(this.zoomLevel - 0.25, 1);
			this.resetPanIfNeeded();
		},

		resetZoom() {
			this.zoomLevel = 1;
			this.panX = 0;
			this.panY = 0;
		},

		toggleZoom() {
			if (this.zoomLevel === 1) {
				this.zoomLevel = 2;
			} else {
				this.resetZoom();
			}
		},

		handleWheel: throttle(function (this: any, event: WheelEvent) {
			event.preventDefault();
			if (event.ctrlKey) {
				this.isZooming = true;
				const delta = -Math.sign(event.deltaY);
				const newZoom = Math.max(1, Math.min(3, this.zoomLevel + delta * 0.1));
				this.zoomLevel = newZoom;
				this.resetPanIfNeeded();
				setTimeout(() => (this.isZooming = false), 100);
			}
		}, 50),

		handlePinch: throttle(function (
			this: any,
			{ event: _event, ...info }: { event: Event } & GestureInfo
		) {
			if (info.isFirst) {
				this.isZooming = true;
				return;
			}
			if (info.isFinal) {
				setTimeout(() => (this.isZooming = false), 100);
				return;
			}
			if (info.distance) {
				const zoomFactor = info.distance / 100;
				this.zoomLevel = Math.max(1, Math.min(3, zoomFactor));
			}
		},
		16),

		handlePan: throttle(function (this: any, { isFirst, isFinal, delta }: GestureInfo) {
			if (this.zoomLevel <= 1) return;
			if (isFirst) this.isDragging = true;
			if (isFinal) {
				this.isDragging = false;
				return;
			}
			this.panX += delta.x;
			this.panY += delta.y;
			const maxPan = 200 * (this.zoomLevel - 1);
			this.panX = Math.max(-maxPan, Math.min(maxPan, this.panX));
			this.panY = Math.max(-maxPan, Math.min(maxPan, this.panY));
		}, 16),

		openFullscreen() {
			this.isFullscreen = true;
			this.resetZoom();
		},

		closeFullscreen() {
			this.isFullscreen = false;
			this.resetZoom();
		}
	},

	mounted() {
		document.addEventListener('keydown', this.handleKeydown);
	},

	beforeUnmount() {
		document.removeEventListener('keydown', this.handleKeydown);
	}
});
